// ============== src/pages/TestListPage.jsx
import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import Select from 'react-select'

// ✅ ใช้คอมโพเนนต์ที่แยกออกมา
import ViewSwitcher from '@/components/ui/ViewSwitcher'

import {
  ClipboardList, Loader, Plus, Pencil,
  ToggleLeft, ToggleRight, FileDown, Gamepad2,
  LayoutGrid, // ใช้ใน MetaChip
  Search, Clock, CheckCircle, Book, ListFilter, ListOrdered
} from 'lucide-react'

import { getTestList, toggleExamStatus } from '@/api/kuizy'
import { formatDateTime, typeExam_Order, typeOption_Order } from '@/settings/master'
import ExamDetailModal from '@/widgets/ExamDetailModal'
import TestExportModal from '@/widgets/TestExportModal'
import PlayerRunModal from '@/widgets/PlayerRunModal'

/** ---------- UI helpers ---------- */
const StatusBadge = ({ active }) => (
  <span
    className={[
      'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium whitespace-nowrap',
      active ? 'bg-green-100 text-green-700' : 'bg-rose-100 text-rose-700'
    ].join(' ')}
  >
    {active ? <CheckCircle size={12} /> : <ToggleLeft size={12} />}
    {active ? 'เปิด' : 'ปิด'}
  </span>
)

const MetaChip = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 rounded-full px-2.5 py-1 text-[11px]">
    <Icon size={12} /> {label}
  </span>
)

export default function TestListPage() {
  const navigate = useNavigate()
  const { cid: cidRaw } = useParams()
  const cidFilter = cidRaw ? decodeURIComponent(cidRaw) : null

  // เก็บข้อมูลทั้งหมดจาก API
  const [allRows, setAllRows] = useState([])
  const [loading, setLoading] = useState(true)

  // false = แสดงที่ "เปิดใช้งาน", true = แสดงที่ "ปิดใช้งาน"
  const [showExpired, setExpired] = useState(false)

  const [view, setView] = useState(() => localStorage.getItem('test_view') || 'card')
  const [searchInput, setSI] = useState('')
  const [searchText, setST] = useState('')

  const [detailOpen, setDetail] = useState(false)
  const [exportOpen, setExport] = useState(false)
  const [selTestId, setSelTest] = useState(null)
  const [selcId, setSelCid] = useState(null)

  const [runModal, setRunModal] = useState({ open: false, test: null })

  useEffect(() => {
    localStorage.setItem('test_view', view)
  }, [view])

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await getTestList(showExpired ? 'inactive' : 'active')
      setAllRows(Array.isArray(data) ? data : [])
    } finally {
      setLoading(false)
    }
  }, [showExpired])

  useEffect(() => { reload() }, [reload])

  const getContentKey = (r) => r.content_id || r.content_code || 'unknown'

  // 1) กรองตาม "เนื้อหา" (จาก URL หรือ select)
  const scopedRows = useMemo(() => {
    if (!cidFilter) return allRows
    return allRows.filter(t => getContentKey(t) === cidFilter)
  }, [allRows, cidFilter])

  // 2) จากนั้นค่อยกรองด้วย "คำค้น" (ชื่อชุดสอบ)
  const filteredRows = useMemo(
    () => scopedRows.filter(t =>
      !searchText || (t.test_name || '').toLowerCase().includes(searchText.toLowerCase())
    ),
    [scopedRows, searchText]
  )


  const grouped = useMemo(() => {
    const acc = {}
    filteredRows.forEach(r => {
      const key = getContentKey(r)
      const name = r.content_name || ''
      const code = r.content_code || ''
      acc[key] ??= { name, code, list: [] }
      acc[key].list.push(r)
    })
    return acc
  }, [filteredRows])

  // ✔ ตัวเลือก “กรองเนื้อหา” จากข้อมูลทั้งหมด (คงเดิมเสมอ) + option “ทั้งหมด”
  const allContentOptions = useMemo(() => {
    const map = new Map()
    allRows.forEach(r => {
      const id = getContentKey(r)
      if (!map.has(id)) {
        const name = r.content_name || ''
        const code = r.content_code || ''
        map.set(id, { value: id, label: name ? (code ? `${name} (${code})` : name) : id })
      }
    })
    return [{ value: '__ALL__', label: 'ทั้งหมด' }, ...Array.from(map.values())]
  }, [allRows])

  const selectedContentOption = useMemo(
    () => allContentOptions.find(o => (cidFilter ? o.value === cidFilter : o.value === '__ALL__')) || allContentOptions[0],
    [allContentOptions, cidFilter]
  )

  const openDetail = t => {
    if (!t.test_id) return
    setSelTest(t.test_id)
    setSelCid(t.content_id)
    setDetail(true)
  }
  const openExport = t => {
    setSelTest(t.test_id)
    setSelCid(t.content_id)
    setExport(true)
  }
  const toggleActive = async t => {
    await toggleExamStatus(t.test_id, !t.active)
    setAllRows(r => r.map(x => x.test_id === t.test_id ? { ...x, active: !x.active } : x))
  }
  const openPlayerRunConfig = t => {
    if (!t?.test_id) return
    setRunModal({ open: true, test: t })
  }

  /** --------- Toolbar --------- */
  const Toolbar = (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: .35, delay: .05 }}
      className="flex flex-col md:flex-row md:items-end gap-4 bg-white rounded-xl shadow-md p-4"
    >
      {/* สถานะ: ปุ่มสั้น ป้องกันตกรอบ */}
      <div className="w-full md:w-[240px]">
        <label className="flex items-center gap-1 text-sm mb-2"><ListFilter size={16} /> สถานะ</label>
        <div className="inline-flex rounded-lg overflow-hidden border border-gray-200">
          <button
            onClick={() => setExpired(false)}
            className={[
              'px-4 py-2 text-sm whitespace-nowrap min-w-[72px]',
              !showExpired ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            ].join(' ')}
          >
            เปิด
          </button>
          <button
            onClick={() => setExpired(true)}
            className={[
              'px-4 py-2 text-sm whitespace-nowrap min-w-[72px]',
              showExpired ? 'bg-rose-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            ].join(' ')}
          >
            ปิด
          </button>
        </div>
      </div>

      {/* กรองเนื้อหา: รายการคงเดิม + มี “ทั้งหมด” (ไม่ต้องเคลียร์) */}
      <div className="min-w-[260px] flex-1">
        <label className="flex items-center gap-1 text-sm"><Book size={16} /> กรองเนื้อหา</label>
        <Select
          options={allContentOptions}
          isClearable={false}
          value={selectedContentOption}
          onChange={opt => {
            if (!opt) return
            if (opt.value === '__ALL__') navigate('/tests')
            else navigate(`/content/${encodeURIComponent(opt.value)}/tests`)
          }}
          placeholder="ทั้งหมด..."
          classNames={{
            control: () => 'border-gray-300 shadow-sm rounded-md min-h-[38px] text-sm',
            menu: () => 'text-sm z-40'
          }}
        />
      </div>

      {/* ค้นหา */}
      <div className="flex flex-col gap-1 flex-1">
        <label className="flex items-center gap-1 text-sm"><Search size={16} /> ค้นหาชุดสอบ</label>
        <div className="relative">
          <input
            value={searchInput}
            onChange={e => setSI(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && setST(searchInput)}
            className="w-full border border-gray-300 rounded-lg px-10 py-2 text-sm focus:ring-2 focus:ring-primary"
            placeholder="พิมพ์ชื่อชุดสอบแล้วกด Enter"
          />
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setST(searchInput)}
          className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm"
        >
          <Search size={16} /> ค้นหา
        </button>
      </div>
    </motion.div>
  )

  return (
    <>
      <motion.section
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: .35 }}
        className="flex flex-col gap-6 p-4 sm:p-6"
      >
        {/* Header: ปุ่มสลับมุมมองกลับมาตำแหน่งเดิม */}
        <motion.header
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: .35, delay: .05 }}
          className="flex flex-wrap items-center justify-between gap-3"
        >
          <h1
            onClick={reload}
            className="flex items-center gap-2 text-xl font-semibold cursor-pointer select-none"
          >
            <ClipboardList size={22} /> ชุดข้อสอบทั้งหมด
            <span className="text-sm text-gray-500">({filteredRows.length})</span>
          </h1>
          {/* ✅ ใช้คอมโพเนนต์ที่แยกออกมา */}
          <ViewSwitcher view={view} setView={setView} />
        </motion.header>

        {Toolbar}

        {/* ===== Card view ===== */}
        {view === 'card' && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: .35, delay: .1 }}
            className="flex flex-col gap-6"
          >
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-44 rounded-xl bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : Object.entries(grouped).length === 0 ? (
              <div className="border-2 border-dashed border-gray-300 rounded-xl py-12 text-center text-gray-500">
                ไม่มีชุดข้อสอบในระบบ
              </div>
            ) : (
              Object.entries(grouped).map(([id, { name, code, list }]) => (
                <motion.div
                  key={id}
                  whileHover={{ y: -2 }}
                  className="bg-white rounded-2xl shadow-md p-6 flex flex-col gap-4"
                >
                  {/* หัวข้อย่อยเนื้อหา */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500">เนื้อหา</span>
                      <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1">
                        <Book size={14} className="text-primary" />
                        <span className="font-medium text-gray-800">{name || id}</span>
                        {code ? <span className="text-[11px] text-gray-500">({code})</span> : null}
                      </span>
                    </div>
                    <button
                      onClick={() => navigate(`/content/${encodeURIComponent(id)}/tests/new`)}
                      className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white rounded-md text-xs px-3 py-1.5"
                    >
                      <Plus size={14} /> เพิ่มชุดข้อสอบสำหรับเนื้อหานี้
                    </button>
                  </div>

                  {list.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center text-gray-500">
                      ไม่พบชุดข้อสอบในเนื้อหานี้
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {list
                        .filter(t => t.test_id != null)
                        .map(t => (
                          <div
                            key={t.test_id}
                            className="relative border border-gray-200 rounded-xl p-4 flex flex-col gap-3 hover:shadow-md transition bg-white"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <button
                                  onClick={() => openDetail(t)}
                                  className="text-left text-gray-900 font-semibold leading-snug hover:text-primary line-clamp-2"
                                  title="ดูรายละเอียดชุดสอบ"
                                >
                                  {t.test_name}
                                </button>
                                {t.description ? (
                                  <p className="text-xs text-gray-500 mt-1 line-clamp-3">{t.description}</p>
                                ) : null}
                              </div>
                              <StatusBadge active={!!t.active} />
                            </div>

                            <div className="flex flex-wrap gap-1.5">
                              <MetaChip icon={LayoutGrid} label={`${t.question_count ?? t.question_ids?.length ?? '-'} ข้อ`} />
                              <MetaChip icon={ListOrdered} label={`เรียงข้อสอบ: ${typeExam_Order[t.exam_order] || 'normal'}`} />
                              <MetaChip icon={ListOrdered} label={`เรียงตัวเลือก: ${typeOption_Order[t.option_order] || 'normal'}`} />
                              <MetaChip icon={CheckCircle} label={`ผ่าน: ${t.criteria ?? 80}%`} />
                              <MetaChip icon={Clock} label={`${t.duration ?? 0} นาที`} />
                            </div>

                            <div className="flex items-end justify-between">
                              <div className="text-[11px] text-gray-500">
                                <p>เริ่ม: {t.start_time ? formatDateTime(t.start_time) : 'ไม่จำกัด'}</p>
                                <p>สิ้นสุด: {t.end_time ? formatDateTime(t.end_time) : 'ไม่จำกัด'}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <button onClick={() => openPlayerRunConfig(t)} className="text-secondary hover:opacity-80" title="ทดลอง/รัน">
                                  <Gamepad2 size={18} />
                                </button>
                                <button onClick={() => navigate(`/content/${t.content_id}/tests/${t.test_id}/edit`)} className="text-primary hover:opacity-80" title="แก้ไข">
                                  <Pencil size={18} />
                                </button>
                                <button onClick={() => toggleActive(t)} className={t.active ? 'text-green-600' : 'text-rose-600'} title="สลับสถานะ">
                                  {t.active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                                </button>
                                <button onClick={() => openExport(t)} className="text-secondary hover:opacity-80" title="ส่งออก/พิมพ์">
                                  <FileDown size={18} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </motion.div>
              ))
            )}
          </motion.div>
        )}

        {/* ===== Table view ===== */}
        {view === 'table' && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: .35, delay: .1 }}
            className="bg-white rounded-xl shadow-md overflow-x-auto"
          >
            <table className="min-w-[920px] w-full text-sm">
              <thead className="bg-gray-100 text-gray-600 whitespace-nowrap">
                <tr>
                  <th className="py-2 px-3 w-14 text-left">#</th>
                  <th className="py-2 px-3 text-left">ชื่อชุดสอบ</th>
                  <th className="py-2 px-3 text-left">เนื้อหา</th>
                  <th className="py-2 px-3 text-center">ข้อ</th>
                  <th className="py-2 px-3 text-center">เกณฑ์ (%)</th>
                  <th className="py-2 px-3 text-center">เวลา</th>
                  <th className="py-2 px-3 text-left md:table-cell hidden">เริ่ม</th>
                  <th className="py-2 px-3 text-left md:table-cell hidden">สิ้นสุด</th>
                  <th className="py-2 px-3 text-center">สถานะ</th>
                  <th className="py-2 px-3 w-36 text-right">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="py-6 text-center">
                      <Loader className="animate-spin" />
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-6 text-center text-gray-500">ไม่พบข้อมูล</td>
                  </tr>
                ) : (
                  filteredRows
                    .filter(t => t.test_id != null)
                    .map((t, i) => (
                      <tr key={t.test_id} className="odd:bg-white even:bg-gray-50">
                        <td className="py-2 px-3">{i + 1}</td>
                        <td className="py-2 px-3">
                          <button onClick={() => openDetail(t)} className="text-primary hover:underline">{t.test_name}</button>
                          {t.description ? <div className="text-[11px] text-gray-500 line-clamp-1">{t.description}</div> : null}
                        </td>
                        <td className="py-2 px-3">{t.content_name || t.content_id}</td>
                        <td className="py-2 px-3 text-center">{t.question_count ?? t.question_ids?.length ?? '-'}</td>
                        <td className="py-2 px-3 text-center">{t.criteria ?? 80}%</td>
                        <td className="py-2 px-3 text-center">{t.duration ?? 0} นาที</td>
                        <td className="py-2 px-3 hidden md:table-cell">{t.start_time ? formatDateTime(t.start_time) : '-'}</td>
                        <td className="py-2 px-3 hidden md:table-cell">{t.end_time ? formatDateTime(t.end_time) : '-'}</td>
                        <td className="py-2 px-3 text-center"><StatusBadge active={!!t.active} /></td>
                        <td className="py-2 px-3 text-right space-x-2">
                          <button onClick={() => openPlayerRunConfig(t)} className="text-secondary hover:opacity-80" title="ทดลอง/รัน"><Gamepad2 size={18} /></button>
                          <button onClick={() => navigate(`/content/${t.content_id}/tests/${t.test_id}/edit`)} className="text-primary hover:opacity-80" title="แก้ไข"><Pencil size={18} /></button>
                          <button onClick={() => toggleActive(t)} className={t.active ? 'text-green-600' : 'text-rose-600'} title="สลับสถานะ">
                            {t.active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                          </button>
                          <button onClick={() => openExport(t)} className="text-secondary hover:opacity-80" title="ส่งออก/พิมพ์"><FileDown size={18} /></button>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </motion.div>
        )}
      </motion.section>

      {/* Modals */}
      <AnimatePresence>
        {detailOpen && (
          <ExamDetailModal
            visible={detailOpen}
            testId={selTestId}
            cid={selcId}
            onClose={() => setDetail(false)}
          />
        )}
        {exportOpen && (
          <TestExportModal
            visible={exportOpen}
            cid={selcId}
            testId={selTestId}
            onClose={() => setExport(false)}
          />
        )}
        {runModal.open && (
          <PlayerRunModal
            open={runModal.open}
            mode="create"
            runId={null}
            testId={runModal.test?.test_id}
            testMeta={runModal.test}
            onClose={() => setRunModal({ open: false, test: null })}
            onSuccess={() => setRunModal({ open: false, test: null })}
          />
        )}
      </AnimatePresence>
    </>
  )
}
